import { STAGED_FILES_SNAPSHOT_ID } from "@/constants";
import { useFilesystemStore } from "@/state/stores/filesystemStore";
import { useParams } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";

/**
 * Returns the snapshotId from the URL params, if present.
 */
export function useParamSnapshotId(): string | undefined {
  return useParams({
    strict: false,
    select: (params) => params.snapshotId as string | undefined,
  });
}

/**
 * Returns the active snapshot id to display. If the URL param is present, it
 * will be used. Otherwise, falls back to "staged-files" (current working file tree)
 */
export function useActiveSnapshotId(): string {
  const paramSnapshotId = useParamSnapshotId();

  if (paramSnapshotId) {
    return paramSnapshotId;
  }

  // Fallback: return the keyword "staged-files", which should show staged files that have not been snapshot-ed yet
  return STAGED_FILES_SNAPSHOT_ID;
}

// TODO - Move this into store
const STAGED_FILES_SNAPSHOT = {
  id: STAGED_FILES_SNAPSHOT_ID,
  label: "Latest",
  createdAt: new Date().toISOString(),
  fileCount: 0, // Will be dynamically calculated based on staged files
  lamportId: Number.MAX_SAFE_INTEGER, // Always show as the latest/current version
};

/**
 * Returns snapshot summaries from the store, sorted ascending by creation time.
 * @TODO - Use the lamport clock to tiebreak snapshots with the same creation time
 */
export function useSortedSnapshots() {
  const sortedSnapshots = useFilesystemStore(
    useShallow((s) =>
      [...s.snapshots]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .concat(STAGED_FILES_SNAPSHOT),
    ),
  );

  return sortedSnapshots;
}
